import { useState, useEffect, useCallback } from 'react';
import './AdminPage.css';
import { API_URL } from '../config';

export const AdminPage = () => {
    const [password, setPassword] = useState('');
    const [authed, setAuthed] = useState(false);
    const [authError, setAuthError] = useState('');
    const [data, setData] = useState(null);
    const [maxInput, setMaxInput] = useState('');
    const [notifyEmails, setNotifyEmails] = useState([]);
    const [nailSlots, setNailSlots] = useState(5);
    const [nailSlotsInput, setNailSlotsInput] = useState('5');
    const [nailSlotsSaving, setNailSlotsSaving] = useState(false);
    const [nailMailSubscribers, setNailMailSubscribers] = useState([]);
    const [orders, setOrders] = useState([]);
    const [artCommissions, setArtCommissions] = useState([]);
    const [fetchError, setFetchError] = useState('');

    const getHeaders = useCallback(() => ({ 'Content-Type': 'application/json', 'x-admin-password': password }), [password]);

    const fetchData = useCallback(async () => {
        const headers = getHeaders();
        setFetchError('');
        try {
            const [commRes, emailRes, invRes] = await Promise.all([
                fetch(`${API_URL}/api/admin/commissions`, { headers }),
                fetch(`${API_URL}/api/admin/notify-emails`, { headers }),
                fetch(`${API_URL}/api/inventory`),
            ]);
            if (!commRes.ok) {
                const err = await commRes.json().catch(() => ({}));
                setFetchError(`failed to load: ${err.error || commRes.status}`);
                return;
            }
            const commData = await commRes.json();
            const emailData = await emailRes.json();
            const invData = await invRes.json();
            setData(commData);
            setMaxInput(String(commData.maxCommissions));
            setNotifyEmails(emailData.emails || []);
            setNailSlots(invData.nailSlots ?? 5);
            setNailSlotsInput(String(invData.nailSlots ?? 5));
            setNailMailSubscribers(commData.nailMailSubscribers || []);
        } catch (err) {
            setFetchError(`could not reach server: ${err.message}`);
        }

        // orders and art commissions fetched separately so a failure doesn't block the rest
        try {
            const [ordersRes, artRes] = await Promise.all([
                fetch(`${API_URL}/api/admin/orders`, { headers }),
                fetch(`${API_URL}/api/admin/art-commissions`, { headers }),
            ]);
            if (ordersRes.ok) {
                const ordersData = await ordersRes.json();
                setOrders(ordersData.orders || []);
            }
            if (artRes.ok) {
                const artData = await artRes.json();
                setArtCommissions(artData.artCommissions || []);
            }
        } catch {}
    }, [getHeaders]);

    useEffect(() => { if (authed) fetchData(); }, [authed, fetchData]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setAuthError('');
        try {
            const res = await fetch(`${API_URL}/api/admin/login`, { method: 'POST', headers: getHeaders() });
            if (res.ok) setAuthed(true);
            else setAuthError('wrong password');
        } catch { setAuthError('could not reach server'); }
    };

    const updateStatus = async (id, status, tracking) => {
        await fetch(`${API_URL}/api/admin/commissions/${id}`, {
            method: 'PATCH', headers: getHeaders(),
            body: JSON.stringify({ status, ...(tracking && { tracking }) }),
        });
        fetchData();
    };

    const deleteCommission = async (id) => {
        if (!window.confirm('delete this commission?')) return;
        await fetch(`${API_URL}/api/admin/commissions/${id}`, { method: 'DELETE', headers: getHeaders() });
        fetchData();
    };

    const updateArtStatus = async (id, status, tracking) => {
        await fetch(`${API_URL}/api/admin/art-commissions/${id}`, {
            method: 'PATCH', headers: getHeaders(),
            body: JSON.stringify({ status, ...(tracking && { tracking }) }),
        });
        setArtCommissions(prev => prev.map(c => c.id === id ? { ...c, status } : c));
    };

    const deleteArtCommission = async (id) => {
        if (!window.confirm('delete this art commission?')) return;
        await fetch(`${API_URL}/api/admin/art-commissions/${id}`, { method: 'DELETE', headers: getHeaders() });
        setArtCommissions(prev => prev.filter(c => c.id !== id));
    };

    const updateMax = async () => {
        const val = parseInt(maxInput, 10);
        if (isNaN(val) || val < 0) return;
        await fetch(`${API_URL}/api/admin/settings`, {
            method: 'PATCH', headers: getHeaders(), body: JSON.stringify({ maxCommissions: val }),
        });
        fetchData();
    };

    const clearNotifyEmails = async () => {
        if (!window.confirm('clear all notify emails?')) return;
        await fetch(`${API_URL}/api/admin/notify-emails`, { method: 'DELETE', headers: getHeaders() });
        setNotifyEmails([]);
    };

    const deleteNailMailSubscriber = async (id) => {
        if (!window.confirm('delete this nail mail subscriber?')) return;
        await fetch(`${API_URL}/api/admin/nailmail/${id}`, { method: 'DELETE', headers: getHeaders() });
        setNailMailSubscribers(prev => prev.filter(s => s.id !== id));
    };

    const updateOrderStatus = async (id, status, tracking) => {
        await fetch(`${API_URL}/api/admin/orders/${id}`, {
            method: 'PATCH', headers: getHeaders(),
            body: JSON.stringify({ status, ...(tracking && { tracking }) }),
        });
        setOrders(prev => prev.map(o => o.id === id ? { ...o, status } : o));
    };

    const deleteOrder = async (id) => {
        if (!window.confirm('delete this order?')) return;
        await fetch(`${API_URL}/api/admin/orders/${id}`, { method: 'DELETE', headers: getHeaders() });
        setOrders(prev => prev.filter(o => o.id !== id));
    };

    const saveNailSlots = async () => {
        const val = parseInt(nailSlotsInput, 10);
        if (isNaN(val) || val < 0) return;
        setNailSlotsSaving(true);
        try {
            await fetch(`${API_URL}/api/admin/nail-slots`, {
                method: 'PATCH', headers: getHeaders(), body: JSON.stringify({ nailSlots: val }),
            });
            setNailSlots(val);
        } catch {}
        setNailSlotsSaving(false);
    };

    if (!authed) {
        return (
            <div className="admin-login">
                <h2>admin</h2>
                <form onSubmit={handleLogin}>
                    <input type="password" placeholder="password" value={password}
                        onChange={(e) => setPassword(e.target.value)} className="admin-input" />
                    <button type="submit" className="admin-btn">log in</button>
                </form>
                {authError && <p className="admin-error">{authError}</p>}
            </div>
        );
    }

    if (!data) return (
        <div className="admin-login">
            <p>{fetchError || 'loading...'}</p>
            {fetchError && <button onClick={fetchData} className="admin-btn">retry</button>}
        </div>
    );

    const open = data.commissions.filter((c) => c.status !== 'completed' && c.status !== 'cancelled');
    const closed = data.commissions.filter((c) => c.status === 'completed' || c.status === 'cancelled');
    const openArt = artCommissions.filter((c) => c.status !== 'completed' && c.status !== 'cancelled');

    const CommissionCard = ({ c }) => {
        const [pendingStatus, setPendingStatus] = useState(c.status);
        const [tracking, setTracking] = useState('');
        const changed = pendingStatus !== c.status;
        return (
            <div className="admin-card">
                <div className="admin-card-header">
                    <span className="admin-card-name">{c.name}</span>
                    <span className={`admin-status admin-status-${c.status}`}>{c.status}</span>
                </div>
                <div className="admin-card-details">
                    <p>shape: {c.shape} \u00b7 length: {c.length} \u00b7 sizes: {c.sizes}</p>
                    <p>contact: {c.contactMethod} \u00b7 {c.contactInfo}</p>
                    <p>date needed: {c.dateNeeded} \u00b7 rush: {c.rushOrder ? 'yes' : 'no'} \u00b7 ${c.price}</p>
                    {c.designDetails && c.designDetails !== 'none' && <p>design: {c.designDetails}</p>}
                    <p className="admin-note">submitted: {new Date(c.createdAt).toLocaleDateString()}</p>
                </div>
                <div className="admin-card-actions">
                    <select value={pendingStatus} onChange={(e) => setPendingStatus(e.target.value)} className="admin-select">
                        <option value="pending">pending</option>
                        <option value="in-progress">in progress</option>
                        <option value="shipped">shipped</option>
                        <option value="completed">completed</option>
                        <option value="cancelled">cancelled</option>
                    </select>
                    {pendingStatus === 'shipped' && (
                        <input type="text" placeholder="tracking number (optional)" value={tracking}
                            onChange={(e) => setTracking(e.target.value)} className="admin-input" style={{ marginTop: 6 }} />
                    )}
                    <button onClick={() => updateStatus(c.id, pendingStatus, pendingStatus === 'shipped' ? tracking : undefined)}
                        disabled={!changed} className="admin-btn">save + notify</button>
                    <button onClick={() => deleteCommission(c.id)} className="admin-btn admin-btn-danger">delete</button>
                </div>
            </div>
        );
    };

    const ArtCommissionCard = ({ c }) => {
        const [pendingStatus, setPendingStatus] = useState(c.status);
        const [tracking, setTracking] = useState('');
        const changed = pendingStatus !== c.status;
        return (
            <div className="admin-card">
                <div className="admin-card-header">
                    <span className="admin-card-name">{c.name}</span>
                    <span className={`admin-status admin-status-${c.status}`}>{c.status}</span>
                </div>
                <div className="admin-card-details">
                    <p>type: {c.artType} \u00b7 size: {c.size}</p>
                    <p>contact: {c.contactMethod} \u00b7 {c.contactInfo}</p>
                    <p>date needed: {c.dateNeeded} \u00b7 rush: {c.rushOrder ? 'yes' : 'no'} \u00b7 ${c.price}</p>
                    {c.description && <p>description: {c.description}</p>}
                    {c.referenceLinks && <p>links: {c.referenceLinks}</p>}
                    <p className="admin-note">submitted: {new Date(c.createdAt).toLocaleDateString()}</p>
                </div>
                <div className="admin-card-actions">
                    <select value={pendingStatus} onChange={(e) => setPendingStatus(e.target.value)} className="admin-select">
                        <option value="pending">pending</option>
                        <option value="in-progress">in progress</option>
                        <option value="completed">completed</option>
                        <option value="cancelled">cancelled</option>
                    </select>
                    {pendingStatus === 'shipped' && (
                        <input type="text" placeholder="tracking number (optional)" value={tracking}
                            onChange={(e) => setTracking(e.target.value)} className="admin-input" style={{ marginTop: 6 }} />
                    )}
                    <button onClick={() => updateArtStatus(c.id, pendingStatus, pendingStatus === 'shipped' ? tracking : undefined)}
                        disabled={!changed} className="admin-btn">save + notify</button>
                    <button onClick={() => deleteArtCommission(c.id)} className="admin-btn admin-btn-danger">delete</button>
                </div>
            </div>
        );
    };

    const OrderCard = ({ o }) => {
        const [pendingStatus, setPendingStatus] = useState(o.status);
        const [tracking, setTracking] = useState('');
        const changed = pendingStatus !== o.status;
        return (
            <div className="admin-card">
                <div className="admin-card-header">
                    <span className="admin-card-name">{o.name}</span>
                    <span className={`admin-status admin-status-${o.status}`}>{o.status}</span>
                </div>
                <div className="admin-card-details">
                    <p>email: {o.email}</p>
                    <p>shipping: {o.shipping?.address1}, {o.shipping?.city}, {o.shipping?.state} {o.shipping?.zip}</p>
                    <p>items: {o.items?.map(i => `${i.title} \u00d7${i.quantity}`).join(', ')}</p>
                    <p>total: ${o.orderTotal?.toFixed(2)} \u00b7 shipping: {o.shippingCost === 0 ? 'free' : `$${o.shippingCost?.toFixed(2)}`}</p>
                    <p className="admin-note">ordered: {new Date(o.createdAt).toLocaleDateString()}</p>
                </div>
                <div className="admin-card-actions">
                    <select value={pendingStatus} onChange={(e) => setPendingStatus(e.target.value)} className="admin-select">
                        <option value="pending">pending</option>
                        <option value="in-progress">in progress</option>
                        <option value="shipped">shipped</option>
                        <option value="completed">completed</option>
                        <option value="cancelled">cancelled</option>
                    </select>
                    {pendingStatus === 'shipped' && (
                        <input type="text" placeholder="tracking number (optional)" value={tracking}
                            onChange={(e) => setTracking(e.target.value)} className="admin-input" style={{ marginTop: 6 }} />
                    )}
                    <button onClick={() => updateOrderStatus(o.id, pendingStatus, pendingStatus === 'shipped' ? tracking : undefined)}
                        disabled={!changed} className="admin-btn">save + notify</button>
                    <button onClick={() => deleteOrder(o.id)} className="admin-btn admin-btn-danger">delete</button>
                </div>
            </div>
        );
    };

    return (
        <div className="admin-container">
            <h2 className="admin-title">\u2727 admin panel</h2>

            <div className="admin-section">
                <h3>shop orders ({orders.filter(o => o.status !== 'completed' && o.status !== 'cancelled').length} pending)</h3>
                {orders.length === 0 && <p className="admin-note">no orders yet</p>}
                {orders.slice().reverse().map((o) => <OrderCard key={o.id} o={o} />)}
            </div>

            <div className="admin-section">
                <h3>nail set inventory</h3>
                <p className="admin-note">how many nail sets you're taking at a time. 0 = sold out for all nails.</p>
                <div className="admin-row">
                    <span>available sets:</span>
                    <input type="number" min="0" value={nailSlotsInput}
                        onChange={(e) => setNailSlotsInput(e.target.value)}
                        className="admin-input admin-input-sm" />
                    <button onClick={saveNailSlots}
                        disabled={nailSlotsSaving || parseInt(nailSlotsInput, 10) === nailSlots}
                        className="admin-btn">
                        {nailSlotsSaving ? 'saving...' : 'save'}
                    </button>
                </div>
                <p className="admin-note">{nailSlots} set{nailSlots !== 1 ? 's' : ''} available right now</p>
            </div>

            <div className="admin-section">
                <h3>commission slots</h3>
                <div className="admin-row">
                    <span>max commissions:</span>
                    <input type="number" value={maxInput} onChange={(e) => setMaxInput(e.target.value)}
                        className="admin-input admin-input-sm" min="0" />
                    <button onClick={updateMax} className="admin-btn">save</button>
                </div>
                <p className="admin-note">{open.length} / {data.maxCommissions} slots filled</p>
            </div>

            <div className="admin-section">
                <h3>active nail commissions ({open.length})</h3>
                {open.length === 0 && <p className="admin-note">no active nail commissions</p>}
                {open.map((c) => <CommissionCard key={c.id} c={c} />)}
            </div>

            <div className="admin-section">
                <h3>art commissions ({openArt.length} active)</h3>
                {artCommissions.length === 0 && <p className="admin-note">no art commissions yet</p>}
                {artCommissions.slice().reverse().map((c) => <ArtCommissionCard key={c.id} c={c} />)}
            </div>

            <div className="admin-section">
                <h3>notify list ({notifyEmails.length})</h3>
                {notifyEmails.length > 0 ? (
                    <>
                        <ul className="admin-email-list">
                            {notifyEmails.map((email, i) => <li key={i}>{email}</li>)}
                        </ul>
                        <button onClick={clearNotifyEmails} className="admin-btn admin-btn-danger">clear all</button>
                    </>
                ) : <p className="admin-note">no emails</p>}
            </div>

            {closed.length > 0 && (
                <div className="admin-section">
                    <h3>closed nail commissions ({closed.length})</h3>
                    {closed.map((c) => (
                        <div key={c.id} className="admin-card admin-card-closed">
                            <div className="admin-card-header">
                                <span className="admin-card-name">{c.name}</span>
                                <span className={`admin-status admin-status-${c.status}`}>{c.status}</span>
                            </div>
                            <p className="admin-note">{c.shape} \u00b7 {c.length} \u00b7 ${c.price} \u00b7 {new Date(c.createdAt).toLocaleDateString()}</p>
                            <button onClick={() => deleteCommission(c.id)} className="admin-btn admin-btn-danger">delete</button>
                        </div>
                    ))}
                </div>
            )}

            <div className="admin-section">
                <h3>nail mail subscribers ({nailMailSubscribers.length})</h3>
                {nailMailSubscribers.length === 0 && <p className="admin-note">no subscribers yet</p>}
                {nailMailSubscribers.map((s) => (
                    <div key={s.id} className="admin-card">
                        <div className="admin-card-header">
                            <span className="admin-card-name">{s.name}</span>
                            <span className={`admin-status admin-status-${s.status}`}>{s.status}</span>
                        </div>
                        <div className="admin-card-details">
                            <p>plan: {s.plan} \u00b7 shape: {s.shape} \u00b7 length: {s.length}</p>
                            <p>sizes: {s.nailSizes}</p>
                            <p>contact: {s.contactMethod} \u00b7 {s.contactInfo}</p>
                            <p>shipping: {s.shipping?.address1}, {s.shipping?.city}, {s.shipping?.state} {s.shipping?.zip}</p>
                            {s.notes && <p>notes: {s.notes}</p>}
                            {s.designPrefs && <p>design prefs: {s.designPrefs}</p>}
                            <p className="admin-note">subscribed: {new Date(s.createdAt).toLocaleDateString()}</p>
                        </div>
                        <div className="admin-card-actions">
                            <button onClick={() => deleteNailMailSubscriber(s.id)} className="admin-btn admin-btn-danger">delete</button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
